import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  HStack,
  Box,
  Text,
  Grid,
  Flex,
} from "@chakra-ui/react";
import { GiftCardFormData } from "../types/Booking";
import { bookingService } from "../services/bookingService";
import RichTextEditor from "./RichTextEditor";

interface GiftCardModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 1 | 2 | 3 | 4;

const initialFormData: GiftCardFormData = {
  Nome: "",
  Cognome: "",
  Email: "",
  Telefono: "",
  NomeDestinatario: "",
  CognomeDestinatario: "",
  EmailDestinatario: "",
  TipoSoggetto: "Privato",
  CodiceFiscale: "",
  PartitaIVA: "",
  RagioneSociale: "",
  Indirizzo: "",
  CAP: "",
  Citta: "",
  Provincia: "",
  Nazione: "Italia",
  NumeroPersone: 1,
  NumeroAlcolico: 0,
  NumeroAnalcolico: 1,
  Note: "",
  Messaggio: "",
};

const GiftCardModal: React.FC<GiftCardModalProps> = ({
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<Step>(1);
  const [formData, setFormData] = useState<GiftCardFormData>(initialFormData);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputStyle = {
    bg: "gray.900",
    border: "1px solid",
    borderColor: "whiteAlpha.300",
    _hover: { borderColor: "whiteAlpha.400" },
    _focus: { borderColor: "white", boxShadow: "0 0 0 1px white" },
    color: "white",
    borderRadius: "none",
  };

  const labelStyle = {
    color: "gray.400",
    fontSize: "xs",
    textTransform: "uppercase" as const,
    letterSpacing: "wide",
    mb: 2,
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "number" ? (value === "" ? 0 : parseInt(value)) : value,
    }));
  };

  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = value === "" ? 0 : parseInt(value);
    setFormData((prev) => ({
      ...prev,
      [name]: numValue,
    }));
  };

  const handleNumeroPersoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numPersone = value === "" ? 0 : parseInt(value);
    setFormData((prev) => ({
      ...prev,
      NumeroPersone: numPersone,
      NumeroAnalcolico: numPersone,
      NumeroAlcolico: 0,
    }));
  };

  const beveragesValid = () => {
    return formData.NumeroAlcolico + formData.NumeroAnalcolico === formData.NumeroPersone && formData.NumeroPersone > 0;
  };

  const isStep1Valid = () => {
    return beveragesValid();
  };

  const isStep2Valid = () => {
    if (!formData.NomeDestinatario.trim()) return false;
    if (!formData.CognomeDestinatario.trim()) return false;
    return true;
  };

  const isStep3Valid = () => {
    if (!formData.Nome.trim() || !formData.Cognome.trim()) return false;
    if (!formData.Email.trim() || !formData.Telefono.trim()) return false;
    if (formData.TipoSoggetto === "Azienda" && !formData.PartitaIVA) return false;
    if (formData.TipoSoggetto === "Privato" && !formData.CodiceFiscale) return false;
    return true;
  };

  const goToStep = (newStep: Step) => {
    setError(null);
    if (newStep === 2 && !isStep1Valid()) {
      setError("La somma delle bevande deve essere uguale al numero di partecipanti");
      return;
    }
    if (newStep === 3 && !isStep2Valid()) {
      setError("Compila i dati del destinatario");
      return;
    }
    if (newStep === 4 && !isStep3Valid()) {
      setError("Compila tutti i campi obbligatori");
      return;
    }
    setStep(newStep);
  };

  const handleSubmit = async () => {
    setError(null);
    try {
      setSaving(true);
      await bookingService.createGiftCard(formData);
      onSuccess();
    } catch (err: any) {
      setError(err.message || "Errore durante la creazione della gift card");
    } finally {
      setSaving(false);
    }
  };

  const StepIndicator = () => {
    const labels = ["DETTAGLI", "DESTINATARIO", "DATI", "RIEPILOGO"];
    return (
      <Flex justify="center" align="center" mb={6} flexShrink={0} px={4}>
        {[1, 2, 3, 4].map((s, i) => (
          <React.Fragment key={s}>
            <VStack spacing={1}>
              <Flex
                w="32px"
                h="32px"
                borderRadius="full"
                border="2px solid"
                borderColor={step >= s ? "white" : "whiteAlpha.300"}
                bg={step > s ? "white" : "transparent"}
                align="center"
                justify="center"
                transition="all 0.3s"
              >
                <Text
                  color={step > s ? "gray.900" : step === s ? "white" : "whiteAlpha.400"}
                  fontWeight="500"
                  fontSize="xs"
                >
                  {step > s ? "✓" : s}
                </Text>
              </Flex>
              <Text
                color={step >= s ? "white" : "whiteAlpha.400"}
                fontSize="2xs"
                textTransform="uppercase"
                letterSpacing="wider"
                fontWeight={step === s ? "500" : "400"}
                display={{ base: "none", md: "block" }}
              >
                {labels[i]}
              </Text>
            </VStack>
            {i < 3 && (
              <Box
                flex={1}
                h="2px"
                bg={step > s ? "white" : "whiteAlpha.300"}
                mx={2}
                mt={{ base: 0, md: "-16px" }}
                transition="all 0.3s"
              />
            )}
          </React.Fragment>
        ))}
      </Flex>
    );
  };

  const renderStep1 = () => (
    <VStack spacing={5} align="stretch">
      <Text textTransform="uppercase" letterSpacing="widest" fontSize="xs" color="gray.500">DETTAGLI GIFT CARD</Text>
      
      <FormControl>
        <FormLabel {...labelStyle}>NUMERO PARTECIPANTI</FormLabel>
        <Input
          type="number"
          name="NumeroPersone"
          value={formData.NumeroPersone || ""}
          onChange={handleNumeroPersoneChange}
          min={1}
          {...inputStyle}
        />
      </FormControl>

      <Box>
        <Text fontSize="xs" color="gray.500" mb={3} textTransform="uppercase" letterSpacing="wide">
          SELEZIONA IL TIPO DI BEVANDA PER OGNI PARTECIPANTE
        </Text>
        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
          <FormControl>
            <FormLabel {...labelStyle}>MENU ALCOLICO</FormLabel>
            <Input
              type="number"
              name="NumeroAlcolico"
              value={formData.NumeroAlcolico || ""}
              onChange={handleNumericChange}
              min={0}
              {...inputStyle}
              borderColor={!beveragesValid() ? "red.500" : "whiteAlpha.300"}
              _hover={{ borderColor: !beveragesValid() ? "red.400" : "whiteAlpha.400" }}
            />
          </FormControl>
          <FormControl>
            <FormLabel {...labelStyle}>MENU ANALCOLICO</FormLabel>
            <Input
              type="number"
              name="NumeroAnalcolico"
              value={formData.NumeroAnalcolico || ""}
              onChange={handleNumericChange}
              min={0}
              {...inputStyle}
              borderColor={!beveragesValid() ? "red.500" : "whiteAlpha.300"}
              _hover={{ borderColor: !beveragesValid() ? "red.400" : "whiteAlpha.400" }}
            />
          </FormControl>
        </Grid>
        {!beveragesValid() && formData.NumeroPersone > 0 && (
          <Text fontSize="xs" color="red.400" mt={2} textTransform="uppercase" letterSpacing="wide">
            {formData.NumeroAlcolico + formData.NumeroAnalcolico < formData.NumeroPersone
              ? `MANCANO ${formData.NumeroPersone - (formData.NumeroAlcolico + formData.NumeroAnalcolico)} BEVANDE`
              : `${formData.NumeroAlcolico + formData.NumeroAnalcolico - formData.NumeroPersone} BEVANDE IN ECCESSO`}
          </Text>
        )}
      </Box>
    </VStack>
  );

  const renderStep2 = () => (
    <VStack spacing={4} align="stretch">
      <Text textTransform="uppercase" letterSpacing="widest" fontSize="xs" color="gray.500">DESTINATARIO GIFT CARD</Text>
      <Text fontSize="xs" color="gray.600" mt={-2} textTransform="uppercase" letterSpacing="wide">
        Inserisci i dati di chi riceverà la gift card
      </Text>

      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
        <FormControl isRequired>
          <FormLabel {...labelStyle}>NOME DESTINATARIO</FormLabel>
          <Input name="NomeDestinatario" value={formData.NomeDestinatario} onChange={handleChange} {...inputStyle} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel {...labelStyle}>COGNOME DESTINATARIO</FormLabel>
          <Input name="CognomeDestinatario" value={formData.CognomeDestinatario} onChange={handleChange} {...inputStyle} />
        </FormControl>
      </Grid>

      <FormControl>
        <FormLabel {...labelStyle}>EMAIL DESTINATARIO</FormLabel>
        <Input
          type="email"
          name="EmailDestinatario"
          value={formData.EmailDestinatario}
          onChange={handleChange}
          placeholder="Inserisci l'email del destinatario per inviare la gift card direttamente"
          {...inputStyle}
          _placeholder={{ color: "gray.600" }}
        />
      </FormControl>

      <Box w="full" h="1px" bg="whiteAlpha.200" my={2} />

      <FormControl>
        <FormLabel {...labelStyle}>NOTE (OPZIONALE)</FormLabel>
        <Textarea
          name="Note"
          value={formData.Note}
          onChange={handleChange}
          placeholder="Eventuali note o richieste speciali..."
          {...inputStyle}
          _placeholder={{ color: "gray.600" }}
          rows={3}
        />
      </FormControl>

      <FormControl>
        <FormLabel {...labelStyle}>MESSAGGIO PERSONALIZZATO (OPZIONALE)</FormLabel>
        <Text fontSize="xs" color="gray.600" mb={2} textTransform="uppercase" letterSpacing="wide">
          Aggiungi un messaggio alla gift card
        </Text>
        <RichTextEditor
          value={formData.Messaggio}
          onChange={(value) => setFormData(prev => ({ ...prev, Messaggio: value }))}
          placeholder="Scrivi qui il tuo messaggio..."
        />
      </FormControl>
    </VStack>
  );

  const renderStep3 = () => (
    <VStack spacing={4} align="stretch">
      <Text textTransform="uppercase" letterSpacing="widest" fontSize="xs" color="gray.500">DATI ACQUIRENTE</Text>

      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
        <FormControl isRequired>
          <FormLabel {...labelStyle}>NOME</FormLabel>
          <Input name="Nome" value={formData.Nome} onChange={handleChange} {...inputStyle} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel {...labelStyle}>COGNOME</FormLabel>
          <Input name="Cognome" value={formData.Cognome} onChange={handleChange} {...inputStyle} />
        </FormControl>
      </Grid>

      <Grid templateColumns="repeat(2, 1fr)" gap={4}>
        <FormControl isRequired>
          <FormLabel {...labelStyle}>EMAIL</FormLabel>
          <Input type="email" name="Email" value={formData.Email} onChange={handleChange} {...inputStyle} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel {...labelStyle}>TELEFONO</FormLabel>
          <Input type="tel" name="Telefono" value={formData.Telefono} onChange={handleChange} {...inputStyle} />
        </FormControl>
      </Grid>

      <Box w="full" h="1px" bg="whiteAlpha.200" my={2} />

      <FormControl isRequired>
        <FormLabel {...labelStyle}>TIPO SOGGETTO</FormLabel>
        <Select name="TipoSoggetto" value={formData.TipoSoggetto} onChange={handleChange} {...inputStyle}>
          <option value="Privato" style={{ background: '#1a1a1a' }}>Privato</option>
          <option value="Azienda" style={{ background: '#1a1a1a' }}>Azienda</option>
        </Select>
      </FormControl>

      {formData.TipoSoggetto === "Privato" ? (
        <FormControl isRequired>
          <FormLabel {...labelStyle}>CODICE FISCALE</FormLabel>
          <Input
            name="CodiceFiscale"
            value={formData.CodiceFiscale}
            onChange={handleChange}
            textTransform="uppercase"
            maxLength={16}
            {...inputStyle}
          />
        </FormControl>
      ) : (
        <>
          <FormControl isRequired>
            <FormLabel {...labelStyle}>PARTITA IVA</FormLabel>
            <Input name="PartitaIVA" value={formData.PartitaIVA} onChange={handleChange} maxLength={11} {...inputStyle} />
          </FormControl>
          <FormControl>
            <FormLabel {...labelStyle}>RAGIONE SOCIALE</FormLabel>
            <Input name="RagioneSociale" value={formData.RagioneSociale} onChange={handleChange} {...inputStyle} />
          </FormControl>
        </>
      )}

      <Box w="full" h="1px" bg="whiteAlpha.200" my={2} />
      <Text textTransform="uppercase" letterSpacing="widest" fontSize="xs" color="gray.600">INDIRIZZO</Text>

      <FormControl>
        <FormLabel {...labelStyle}>VIA/PIAZZA</FormLabel>
        <Input name="Indirizzo" value={formData.Indirizzo} onChange={handleChange} {...inputStyle} />
      </FormControl>

      <Grid templateColumns="repeat(3, 1fr)" gap={4}>
        <FormControl>
          <FormLabel {...labelStyle}>CAP</FormLabel>
          <Input name="CAP" value={formData.CAP} onChange={handleChange} maxLength={10} {...inputStyle} />
        </FormControl>
        <FormControl>
          <FormLabel {...labelStyle}>CITTÀ</FormLabel>
          <Input name="Citta" value={formData.Citta} onChange={handleChange} {...inputStyle} />
        </FormControl>
        <FormControl>
          <FormLabel {...labelStyle}>PROVINCIA</FormLabel>
          <Input name="Provincia" value={formData.Provincia} onChange={handleChange} maxLength={2} textTransform="uppercase" {...inputStyle} />
        </FormControl>
      </Grid>

      <FormControl>
        <FormLabel {...labelStyle}>NAZIONE</FormLabel>
        <Input name="Nazione" value={formData.Nazione} onChange={handleChange} {...inputStyle} />
      </FormControl>
    </VStack>
  );

  const renderStep4 = () => (
    <VStack spacing={4} align="stretch">
      <Text textTransform="uppercase" letterSpacing="widest" fontSize="xs" color="gray.500">RIEPILOGO GIFT CARD</Text>

      <Box bg="whiteAlpha.50" p={4} border="1px solid" borderColor="whiteAlpha.200">
        <Text fontWeight="medium" mb={3} textTransform="uppercase" letterSpacing="wide" fontSize="xs" color="gray.400">DATI ACQUIRENTE</Text>
        <VStack align="stretch" spacing={2} fontSize="sm">
          <HStack justify="space-between">
            <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">NOME</Text>
            <Text color="white">{formData.Nome} {formData.Cognome}</Text>
          </HStack>
          <HStack justify="space-between">
            <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">EMAIL</Text>
            <Text color="white">{formData.Email}</Text>
          </HStack>
          <HStack justify="space-between">
            <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">TELEFONO</Text>
            <Text color="white">{formData.Telefono}</Text>
          </HStack>
          {formData.Indirizzo && (
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">INDIRIZZO</Text>
              <Text color="white" textAlign="right">{formData.Indirizzo}, {formData.CAP} {formData.Citta}</Text>
            </HStack>
          )}
        </VStack>
      </Box>

      {(formData.NomeDestinatario || formData.EmailDestinatario) && (
        <Box bg="whiteAlpha.100" p={4} border="1px solid" borderColor="whiteAlpha.300">
          <Text fontWeight="medium" mb={3} textTransform="uppercase" letterSpacing="wide" fontSize="xs" color="gray.400">DESTINATARIO</Text>
          <VStack align="stretch" spacing={2} fontSize="sm">
            {formData.NomeDestinatario && (
              <HStack justify="space-between">
                <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">NOME</Text>
                <Text color="white">{formData.NomeDestinatario} {formData.CognomeDestinatario}</Text>
              </HStack>
            )}
            {formData.EmailDestinatario && (
              <HStack justify="space-between">
                <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">EMAIL</Text>
                <Text color="white">{formData.EmailDestinatario}</Text>
              </HStack>
            )}
          </VStack>
        </Box>
      )}

      <Box bg="whiteAlpha.50" p={4} border="1px solid" borderColor="whiteAlpha.200">
        <Text fontWeight="medium" mb={3} textTransform="uppercase" letterSpacing="wide" fontSize="xs" color="gray.400">DETTAGLIO GIFT CARD</Text>
        <VStack align="stretch" spacing={2}>
          <HStack justify="space-between">
            <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">PARTECIPANTI</Text>
            <Text color="white">{formData.NumeroPersone}</Text>
          </HStack>
          {formData.NumeroAlcolico > 0 && (
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">ALCOLICHE</Text>
              <Text color="white">{formData.NumeroAlcolico}</Text>
            </HStack>
          )}
          {formData.NumeroAnalcolico > 0 && (
            <HStack justify="space-between">
              <Text color="gray.500" fontSize="xs" textTransform="uppercase" letterSpacing="wide">ANALCOLICHE</Text>
              <Text color="white">{formData.NumeroAnalcolico}</Text>
            </HStack>
          )}
        </VStack>
      </Box>

      {formData.Note && (
        <Box bg="whiteAlpha.50" p={4} border="1px solid" borderColor="whiteAlpha.200">
          <Text fontWeight="medium" mb={2} textTransform="uppercase" letterSpacing="wide" fontSize="xs" color="gray.400">NOTE</Text>
          <Text color="gray.300" fontSize="sm">{formData.Note}</Text>
        </Box>
      )}

      {formData.Messaggio && formData.Messaggio !== '<p></p>' && (
        <Box bg="whiteAlpha.50" p={4} border="1px solid" borderColor="whiteAlpha.200">
          <Text fontWeight="medium" mb={2} textTransform="uppercase" letterSpacing="wide" fontSize="xs" color="gray.400">MESSAGGIO</Text>
          <Box
            color="gray.300"
            fontSize="sm"
            dangerouslySetInnerHTML={{ __html: formData.Messaggio }}
            sx={{
              "& p": { margin: 0 },
              "& strong": { fontWeight: "bold" },
              "& em": { fontStyle: "italic" },
            }}
          />
        </Box>
      )}

      <Box bg="whiteAlpha.50" p={4} border="1px solid" borderColor="whiteAlpha.200">
        <Text fontSize="xs" color="gray.400" textTransform="uppercase" letterSpacing="wide" textAlign="center">
          Il codice della gift card verrà generato automaticamente e inviato via email dopo il pagamento
        </Text>
      </Box>
    </VStack>
  );

  return (
    <Modal isOpen onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(8px)" />
      <ModalContent bg="gray.900" mx={4} borderRadius="none" border="1px solid" borderColor="whiteAlpha.200">
        <ModalHeader
          color="white"
          fontWeight="400"
          textTransform="uppercase"
          letterSpacing="widest"
          fontSize="sm"
          borderBottom="1px solid"
          borderColor="whiteAlpha.100"
          py={4}
        >
          RICHIEDI GIFT CARD
        </ModalHeader>
        <ModalCloseButton color="gray.400" _hover={{ color: "white" }} />

        <Box pt={4}>
          <StepIndicator />
        </Box>

        {error && (
          <Box mx={6} mb={4} p={3} bg="red.900" border="1px solid" borderColor="red.500">
            <Text color="red.300" fontSize="xs" textTransform="uppercase" letterSpacing="wide">{error}</Text>
          </Box>
        )}

        <ModalBody py={4}>
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="whiteAlpha.100" gap={3}>
          {step === 1 && (
            <>
              <Button
                bg="transparent"
                color="gray.400"
                borderRadius="none"
                fontSize="xs"
                letterSpacing="wide"
                textTransform="uppercase"
                _hover={{ color: "white" }}
                onClick={onClose}
              >
                ANNULLA
              </Button>
              <Button
                bg="white"
                color="gray.900"
                borderRadius="none"
                fontSize="xs"
                letterSpacing="wide"
                textTransform="uppercase"
                _hover={{ bg: "gray.200" }}
                onClick={() => goToStep(2)}
                isDisabled={!isStep1Valid()}
              >
                AVANTI
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button
                bg="transparent"
                color="gray.400"
                borderRadius="none"
                fontSize="xs"
                letterSpacing="wide"
                textTransform="uppercase"
                _hover={{ color: "white" }}
                onClick={() => setStep(1)}
              >
                INDIETRO
              </Button>
              <Button
                bg="white"
                color="gray.900"
                borderRadius="none"
                fontSize="xs"
                letterSpacing="wide"
                textTransform="uppercase"
                _hover={{ bg: "gray.200" }}
                onClick={() => goToStep(3)}
                isDisabled={!isStep2Valid()}
              >
                AVANTI
              </Button>
            </>
          )}
          {step === 3 && (
            <>
              <Button
                bg="transparent"
                color="gray.400"
                borderRadius="none"
                fontSize="xs"
                letterSpacing="wide"
                textTransform="uppercase"
                _hover={{ color: "white" }}
                onClick={() => setStep(2)}
              >
                INDIETRO
              </Button>
              <Button
                bg="white"
                color="gray.900"
                borderRadius="none"
                fontSize="xs"
                letterSpacing="wide"
                textTransform="uppercase"
                _hover={{ bg: "gray.200" }}
                onClick={() => goToStep(4)}
                isDisabled={!isStep3Valid()}
              >
                AVANTI
              </Button>
            </>
          )}
          {step === 4 && (
            <>
              <Button
                bg="transparent"
                color="gray.400"
                borderRadius="none"
                fontSize="xs"
                letterSpacing="wide"
                textTransform="uppercase"
                _hover={{ color: "white" }}
                onClick={() => setStep(3)}
              >
                INDIETRO
              </Button>
              <Button
                bg="white"
                color="gray.900"
                borderRadius="none"
                fontSize="xs"
                letterSpacing="wide"
                textTransform="uppercase"
                _hover={{ bg: "gray.200" }}
                onClick={handleSubmit}
                isLoading={saving}
              >
                RICHIEDI
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default GiftCardModal;
